import React, { useState, useMemo, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Platform,
  TouchableOpacity,
  Dimensions,
  ViewStyle,
  Modal,
  ScrollView,
} from 'react-native';
import { useSelector } from 'react-redux';
import Svg, { Path } from 'react-native-svg';
import DLODropdown from '../DLODropdown';
import DLOCreateScheduleModal, { CreateScheduleData } from '../DLOCreateScheduleModal';
import DLORangeFilter, { RangeFilterValue } from './DLORangeFilter';
import { DLODropdownOption } from '../../types/dlo.types';
import { ScheduleRowData } from './DLOScheduleTableContent';
import { getPeriodOptions, getYearOptions, getWeekOptions } from '../../../../utils/filterOptions';
import { formatUserName } from '../../../../utils/emailTouserName';

// Icons from the Figma design
const ArrowDropDownIcon = ({ color = '#005CBC' }) => (
  <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <Path d="M4.66667 6.66675L8 10.0001L11.3333 6.66675H4.66667Z" fill={color} />
  </Svg>
);

const EditIcon = ({ color = '#A0A0A0' }) => (
  <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <Path 
      d="M9.37333 6.01333L9.98667 6.62667L3.94667 12.6667H3.33333V12.0533L9.37333 6.01333ZM11.7733 2C11.6067 2 11.4333 2.06667 11.3067 2.19333L10.0867 3.41333L12.5867 5.91333L13.8067 4.69333C14.0667 4.43333 14.0667 4.01333 13.8067 3.75333L12.2467 2.19333C12.1133 2.06 11.9467 2 11.7733 2ZM9.37333 4.12667L2 11.5V14H4.5L11.8733 6.62667L9.37333 4.12667Z" 
      fill={color} 
    />
  </Svg>
);

const ContentCopyIcon = ({ color = '#A0A0A0' }) => (
  <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <Path 
      d="M10.6666 0.666748H2.66665C1.93331 0.666748 1.33331 1.26675 1.33331 2.00008V11.3334H2.66665V2.00008H10.6666V0.666748ZM12.6666 3.33341H5.33331C4.59998 3.33341 3.99998 3.93341 3.99998 4.66675V14.0001C3.99998 14.7334 4.59998 15.3334 5.33331 15.3334H12.6666C13.4 15.3334 14 14.7334 14 14.0001V4.66675C14 3.93341 13.4 3.33341 12.6666 3.33341ZM12.6666 14.0001H5.33331V4.66675H12.6666V14.0001Z" 
      fill={color} 
    />
  </Svg>
);

const DeleteIcon = ({ color = '#A0A0A0' }) => (
  <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <Path 
      d="M10.6667 6V12.6667H5.33337V6H10.6667ZM9.66671 2H6.33337L5.66671 2.66667H3.33337V4H12.6667V2.66667H10.3334L9.66671 2ZM12 4.66667H4.00004V12.6667C4.00004 13.4 4.60004 14 5.33337 14H10.6667C11.4 14 12 13.4 12 12.6667V4.66667Z" 
      fill={color} 
    />
  </Svg>
);

const FilterIcon = ({ color = '#005CBC' }) => (
  <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <Path 
      d="M6 13H10V11H6V13ZM0 3V5H16V3H0ZM3 9H13V7H3V9Z" 
      fill={color} 
    />
  </Svg>
);

const ResetIcon = ({ color = '#005CBC' }) => (
  <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <Path 
      d="M8 2C4.68667 2 2 4.68667 2 8C2 11.3133 4.68667 14 8 14C11.3133 14 14 11.3133 14 8H12.6667C12.6667 10.5733 10.5733 12.6667 8 12.6667C5.42667 12.6667 3.33333 10.5733 3.33333 8C3.33333 5.42667 5.42667 3.33333 8 3.33333C9.46 3.33333 10.7533 3.94 11.6733 4.86L9.33333 7.2H14V2.86667L12.24 4.62667C11.0267 3.41333 9.58 2.66667 8 2Z" 
      fill={color} 
    />
  </Svg>
);

// Badge Component for All Filters button
interface BadgeProps {
  count: number;
}

const Badge: React.FC<BadgeProps> = ({ count }) => (
  <View style={styles.badge}>
    <Text style={styles.badgeText}>{count}</Text>
  </View>
);

// Filter Button Component
interface FilterButtonProps {
  label: string;
  onPress?: () => void;
  style?: ViewStyle;
}

const FilterButton: React.FC<FilterButtonProps> = ({ label, onPress, style }) => (
  <TouchableOpacity style={[styles.filterButton, style]} onPress={onPress} activeOpacity={0.7}>
    <Text style={styles.filterButtonText}>{label}</Text>
    <ArrowDropDownIcon />
  </TouchableOpacity>
);

// Filter Dropdown Component that looks like FilterButton but uses DLODropdown
interface FilterDropdownProps {
  label: string;
  value?: DLODropdownOption | null;
  options: DLODropdownOption[];
  onSelect: (option: DLODropdownOption | null) => void;
  style?: ViewStyle;
}

// Custom Filter Dropdown Component that exactly matches FilterButton design
const FilterDropdown: React.FC<FilterDropdownProps> = ({ label, value, options, onSelect, style }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number; width: number }>({ top: 0, left: 0, width: 0 });
  const buttonRef = useRef<any>(null);

  const handleToggle = () => {
    if (buttonRef.current) {
      buttonRef.current.measure((x: number, y: number, width: number, height: number, pageX: number, pageY: number) => {
        setPosition({
          top: pageY + height + 2,
          left: pageX,
          width: width, // Use the exact width of the button
        });
        setIsOpen(!isOpen);
      });
    }
  };

  const handleSelect = (option: DLODropdownOption) => {
    onSelect(option);
    setIsOpen(false);
  };

  const handleClear = () => {
    onSelect(null);
    setIsOpen(false);
  };

  const displayText = value ? value.label : label;
  const hasValue = !!value;

  return (
    <>
      <TouchableOpacity 
        ref={buttonRef}
        style={[styles.filterButton, style]} 
        onPress={handleToggle} 
        activeOpacity={0.7}
      >
        <Text style={[
          styles.filterButtonText,
          hasValue && styles.filterButtonTextSelected
        ]}>
          {displayText}
        </Text>
        <ArrowDropDownIcon color={hasValue ? '#005CBC' : '#005CBC'} />
      </TouchableOpacity>

      {isOpen && (
        <Modal
          visible={isOpen}
          transparent
          animationType="fade"
          onRequestClose={() => setIsOpen(false)}
        >
          <TouchableOpacity 
            style={styles.dropdownOverlay} 
            activeOpacity={1} 
            onPress={() => setIsOpen(false)}
          >
            <View 
              style={[
                styles.dropdownMenu,
                {
                  position: 'absolute',
                  top: position.top,
                  left: position.left,
                  width: position.width, // Use exact width instead of minWidth
                }
              ]}
            >
              <ScrollView 
                style={styles.dropdownScrollView}
                showsVerticalScrollIndicator={true}
                nestedScrollEnabled={true}
              >
                {/* Clear option if there's a value */}
                {hasValue && (
                  <>
                    <TouchableOpacity 
                      style={styles.dropdownItem} 
                      onPress={handleClear}
                    >
                      <Text style={styles.dropdownItemText}>Clear</Text>
                    </TouchableOpacity>
                    <View style={styles.dropdownSeparator} />
                  </>
                )}
                
                {/* Options */}
                {options.map((option) => (
                  <TouchableOpacity 
                    key={option.id}
                    style={[
                      styles.dropdownItem,
                      value?.id === option.id && styles.dropdownItemSelected
                    ]} 
                    onPress={() => handleSelect(option)}
                  >
                    <Text style={[
                      styles.dropdownItemText,
                      value?.id === option.id && styles.dropdownItemTextSelected
                    ]}>
                      {option.label}
                    </Text>
                  </TouchableOpacity>
                ))}
              </ScrollView>
            </View>
          </TouchableOpacity>
        </Modal>
      )}
    </>
  );
};

// All Filters Button Component
interface AllFiltersButtonProps {
  isExpanded: boolean;
  filterCount?: number;
  onPress?: () => void;
}

const AllFiltersButton: React.FC<AllFiltersButtonProps> = ({ isExpanded, filterCount = 0, onPress }) => (
  <TouchableOpacity
    style={[styles.allFiltersButton, isExpanded && styles.allFiltersButtonExpanded]}
    onPress={onPress}
    activeOpacity={0.7}
  >
    <FilterIcon color="#FFF" />
    <Text style={[styles.allFiltersButtonText, isExpanded && styles.allFiltersButtonTextExpanded]}>
      {isExpanded ? 'Less Filters' : 'More Filters'}
    </Text>
    {filterCount > 0 && <Badge count={filterCount} />}
  </TouchableOpacity>
);

// Reset All Button Component
interface ResetAllButtonProps {
  onPress?: () => void;
}

const ResetAllButton: React.FC<ResetAllButtonProps> = ({ onPress }) => (
  <TouchableOpacity style={styles.resetAllButton} onPress={onPress} activeOpacity={0.7}>
    <ResetIcon />
    <Text style={styles.resetAllButtonText}>Reset all</Text>
  </TouchableOpacity>
);

// Action Button Component
interface ActionButtonProps {
  icon: React.ReactNode;
  label: string;
  disabled?: boolean;
  onPress?: () => void;
}

const ActionButton: React.FC<ActionButtonProps> = ({ icon, label, disabled = true, onPress }) => (
  <TouchableOpacity 
    style={[styles.actionButton, disabled && styles.actionButtonDisabled]} 
    onPress={onPress}
    activeOpacity={disabled ? 1 : 0.7}
    disabled={disabled}
  >
    {icon}
    <Text style={[styles.actionButtonText, disabled && styles.actionButtonTextDisabled]}>
      {label}
    </Text>
  </TouchableOpacity>
);

// Main Component Props
export interface DLOScheduleTableHeaderProps {
  selectedCount?: number;
  tableData?: ScheduleRowData[];
  selectedScheduleData?: ScheduleRowData | null; // Selected schedule data for copy operations
  // Filter values
  periodFilter?: DLODropdownOption | null;
  weekFilter?: DLODropdownOption | null;
  yearFilter?: DLODropdownOption | null;
  forecastedHoursFilter?: DLODropdownOption | null;
  scheduledHoursFilter?: DLODropdownOption | null;
  actualHoursFilter?: DLODropdownOption | null;
  actualCasesFilter?: DLODropdownOption | null;
  createdByFilter?: DLODropdownOption | null;
  modifiedByFilter?: DLODropdownOption | null;
  // Range filter values
  headcountRangeFilter?: RangeFilterValue | null;
  actualKcphRangeFilter?: RangeFilterValue | null;
  forecastedHoursRangeFilter?: RangeFilterValue | null;
  forecastedCasesRangeFilter?: RangeFilterValue | null;
  actualCasesRangeFilter?: RangeFilterValue | null;
  // Filter change handlers
  onPeriodFilterChange?: (option: DLODropdownOption | null) => void;
  onWeekFilterChange?: (option: DLODropdownOption | null) => void;
  onYearFilterChange?: (option: DLODropdownOption | null) => void;
  onForecastedHoursFilterChange?: (option: DLODropdownOption | null) => void;
  onScheduledHoursFilterChange?: (option: DLODropdownOption | null) => void;
  onActualHoursFilterChange?: (option: DLODropdownOption | null) => void;
  onActualCasesFilterChange?: (option: DLODropdownOption | null) => void;
  onCreatedByFilterChange?: (option: DLODropdownOption | null) => void;
  onModifiedByFilterChange?: (option: DLODropdownOption | null) => void;
  // Range filter change handlers
  onHeadcountRangeFilterChange?: (value: RangeFilterValue | null) => void;
  onActualKcphRangeFilterChange?: (value: RangeFilterValue | null) => void;
  onForecastedHoursRangeFilterChange?: (value: RangeFilterValue | null) => void;
  onForecastedCasesRangeFilterChange?: (value: RangeFilterValue | null) => void;
  onActualCasesRangeFilterChange?: (value: RangeFilterValue | null) => void;
  // Legacy press handlers (deprecated)
  onPeriodFilterPress?: () => void;
  onWeekFilterPress?: () => void;
  onYearFilterPress?: () => void;
  onForecastedCasesFilterPress?: () => void;
  onActualCasesFilterPress?: () => void;
  onCreatedByFilterPress?: () => void;
  onModifiedByFilterPress?: () => void;
  onAllFiltersPress?: () => void;
  onResetAllPress?: () => void;
  onEditPress?: () => void;
  onCopySchedulePress?: () => void;
  onCopyScheduleFromTable?: (targetPeriod: number, targetWeek: number, targetYear: number, selectedScheduleData: ScheduleRowData) => void; // New handler for copy schedule from table
  onDeletePress?: () => void;
  style?: ViewStyle;
}

const DLOScheduleTableHeader: React.FC<DLOScheduleTableHeaderProps> = ({
  selectedCount = 0,
  tableData = [],
  selectedScheduleData = null,
  // Filter values
  periodFilter,
  weekFilter,
  yearFilter,
  forecastedHoursFilter,
  scheduledHoursFilter,
  actualHoursFilter,
  actualCasesFilter,
  createdByFilter,
  modifiedByFilter,
  // Range filter values
  headcountRangeFilter,
  actualKcphRangeFilter,
  forecastedHoursRangeFilter,
  forecastedCasesRangeFilter,
  actualCasesRangeFilter,
  // Filter change handlers
  onPeriodFilterChange,
  onWeekFilterChange,
  onYearFilterChange,
  onForecastedHoursFilterChange,
  onScheduledHoursFilterChange,
  onActualHoursFilterChange,
  onActualCasesFilterChange,
  onCreatedByFilterChange,
  onModifiedByFilterChange,
  // Range filter change handlers
  onHeadcountRangeFilterChange,
  onActualKcphRangeFilterChange,
  onForecastedHoursRangeFilterChange,
  onForecastedCasesRangeFilterChange,
  onActualCasesRangeFilterChange,
  // Legacy handlers
  onPeriodFilterPress,
  onWeekFilterPress,
  onYearFilterPress,
  onForecastedCasesFilterPress,
  onActualCasesFilterPress,
  onCreatedByFilterPress,
  onModifiedByFilterPress,
  onAllFiltersPress,
  onResetAllPress,
  onEditPress,
  onCopySchedulePress,
  onCopyScheduleFromTable,
  onDeletePress,
  style,
}) => {
  const [isFiltersExpanded, setIsFiltersExpanded] = useState(false);
  const [showCopyScheduleModal, setShowCopyScheduleModal] = useState(false);
  const screenWidth = Dimensions.get('window').width;
  const isSmallScreen = screenWidth < 768;
  const isMediumScreen = screenWidth >= 768 && screenWidth < 1024;

  // Get current period/week/year from appData
  const periodWeekData = useSelector((state: any) => state.appData.periodWeek?.data);
  const currentPeriod = periodWeekData?.period || 1;
  const currentWeek = periodWeekData?.week || 1;
  const currentYear = periodWeekData?.year || new Date().getFullYear();

  // Responsive layout
  const getResponsiveStyles = () => {
    if (isSmallScreen) {
      return {
        headerPadding: 8,
        filterContainerPadding: 12,
        filterGap: 6,
        bulkActionsPadding: 8,
        titleFontSize: 20,
        filtersWrapperFlex: 1, // Full width on small screens
        actionButtonsWrapperFlex: 0, // Stack vertically on small screens
      };
    } else if (isMediumScreen) {
      return {
        headerPadding: 8,
        filterContainerPadding: 16,
        filterGap: 8,
        bulkActionsPadding: 12,
        titleFontSize: 22,
        filtersWrapperFlex: 0.7, // 70% on medium screens
        actionButtonsWrapperFlex: 0.3, // 30% on medium screens
      };
    } else {
      return {
        headerPadding: 8,
        filterContainerPadding: 20,
        filterGap: 8,
        bulkActionsPadding: 12,
        titleFontSize: 24,
        filtersWrapperFlex: 0.75, // 75% on large screens
        actionButtonsWrapperFlex: 0.25, // 25% on large screens
      };
    }
  };

  // Extract unique values from table data for dropdown options
  const periodOptions = useMemo(() => {
    return getPeriodOptions().map(option => ({
      id: option.id,
      label: option.displayText,
      value: option.id,
    }));
  }, []);

  const weekOptions = useMemo(() => {
    return getWeekOptions().map(option => ({
      id: option.id,
      label: option.displayText,
      value: option.id,
    }));
  }, []);

  const yearOptions = useMemo(() => {
    return getYearOptions().map(option => ({
      id: option.id,
      label: option.displayText,
      value: option.id,
    }));
  }, []);

  const forecastedHoursOptions = useMemo(() => {
    const uniqueValues = [...new Set(tableData.map(row => row.forecastedCases))].filter(Boolean);
    return uniqueValues.map(value => ({
      id: value,
      label: value,
      value: value,
    }));
  }, [tableData]);

  const scheduledHoursOptions = useMemo(() => {
    const uniqueValues = [...new Set(tableData.map(row => (row as any).scheduledHours))].filter(Boolean);
    return uniqueValues.map(value => ({
      id: value,
      label: value,
      value: value,
    }));
  }, [tableData]);

  const actualHoursOptions = useMemo(() => {
    const uniqueValues = [...new Set(tableData.map(row => (row as any).actualHours))].filter(Boolean);
    return uniqueValues.map(value => ({
      id: value,
      label: value,
      value: value,
    }));
  }, [tableData]);

  const actualCasesOptions = useMemo(() => {
    const uniqueValues = [...new Set(tableData.map(row => row.actualCases))].filter(Boolean);
    return uniqueValues.map(value => ({
      id: value,
      label: value,
      value: value,
    }));
  }, [tableData]);

  const createdByOptions = useMemo(() => {
    // Get unique emails and filter out empty ones
    const uniqueEmails = [...new Set(tableData.map(row => row.createdByEmail).filter(Boolean))] as string[];
    return uniqueEmails.map(email => ({
      id: email,
      label: formatUserName(email),
      value: email,
    }));
  }, [tableData]);

  const modifiedByOptions = useMemo(() => {
    // Get unique emails and filter out empty ones
    const uniqueEmails = [...new Set(tableData.map(row => row.modifiedByEmail).filter(Boolean))] as string[];
    return uniqueEmails.map(email => ({
      id: email,
      label: formatUserName(email),
      value: email,
    }));
  }, [tableData]);

  const responsiveStyles = getResponsiveStyles();

  const handleAllFiltersPress = () => {
    setIsFiltersExpanded(!isFiltersExpanded);
    onAllFiltersPress?.();
  };

  const handleResetAllPress = () => {
    setIsFiltersExpanded(false);
    onResetAllPress?.();
  };

  // Copy Schedule from Table handlers
  const handleCopyScheduleFromTablePress = () => {
    if (selectedCount === 1 && selectedScheduleData) {
      console.log('Opening copy schedule modal for selected schedule:', selectedScheduleData);
      setShowCopyScheduleModal(true);
    } else if (selectedCount > 1) {
      console.warn('Copy schedule only supports single selection');
    } else {
      console.warn('No schedule selected for copy operation');
    }
  };

  const handleCopyScheduleModalClose = () => {
    setShowCopyScheduleModal(false);
  };

  const handleCopyScheduleFromModal = (data: CreateScheduleData) => {
    console.log('Copy Schedule from table modal:', data);
    
    if (!selectedScheduleData) {
      console.error('No selected schedule data available');
      return;
    }

    if (!data.period || !data.week || !data.year) {
      console.error('Period, week, and year are required');
      return;
    }

    const targetPeriod = parseInt(data.period.value, 10);
    const targetWeek = parseInt(data.week.value, 10);
    const targetYear = parseInt(data.year.value, 10);
    
    console.log('Calling copy schedule from table with:', {
      targetPeriod,
      targetWeek,
      targetYear,
      selectedScheduleData
    });

    // Close the modal
    setShowCopyScheduleModal(false);
    
    // Call the parent handler to perform the API call and navigation
    onCopyScheduleFromTable?.(targetPeriod, targetWeek, targetYear, selectedScheduleData);
  };

  // Calculate the number of selected filters (non-null, non-empty)
  const selectedFilters = [
    periodFilter,
    weekFilter,
    yearFilter,
    forecastedHoursFilter,
    scheduledHoursFilter,
    actualHoursFilter,
    actualCasesFilter,
    createdByFilter,
    modifiedByFilter,
  ];
  const selectedRangeFilters = [
    headcountRangeFilter,
    actualKcphRangeFilter,
    forecastedHoursRangeFilter,
    forecastedCasesRangeFilter,
    actualCasesRangeFilter,
  ];
  
  const dropdownFilterCount = selectedFilters.filter(
    f => f && f.value !== undefined && f.value !== null && f.value !== ''
  ).length;
  
  const rangeFilterCount = selectedRangeFilters.filter(
    f => f && (f.min !== undefined || f.max !== undefined)
  ).length;
  
  const filterCount = dropdownFilterCount + rangeFilterCount;

  return (
      <View style={[styles.container, style]}>
        {/* Main Header */}
        <View style={styles.headerContainer}>
          <View style={[styles.headerContent, { padding: responsiveStyles.headerPadding }]}> 
            <Text style={[styles.title, { fontSize: responsiveStyles.titleFontSize }]}> 
              Schedules
            </Text>
          </View>

          {/* Filters Section */}
          <View style={styles.filtersContainer}>
            <View style={[styles.filtersContent, { 
              paddingVertical: responsiveStyles.filterContainerPadding,
              paddingHorizontal: responsiveStyles.headerPadding,
            }]}> 
              {/* Filter buttons */}
              <View style={[
                styles.filtersRow, 
                { gap: responsiveStyles.filterGap },
                isSmallScreen && { flexDirection: 'column', alignItems: 'stretch' }
              ]}> 
                {/* Filters Container - 75% width */}
                <View style={[styles.filtersWrapper, { flex: responsiveStyles.filtersWrapperFlex }]}>
                  {!isFiltersExpanded ? (
                    // Collapsed state - single row with first 5 filters
                    <View style={styles.filterRowSingle}>
                      <FilterDropdown 
                        label="Period" 
                        value={periodFilter}
                        options={periodOptions}
                        onSelect={onPeriodFilterChange || (() => {})}
                        style={styles.filterButtonCollapsed}
                      />
                      <FilterDropdown 
                        label="Week" 
                        value={weekFilter}
                        options={weekOptions}
                        onSelect={onWeekFilterChange || (() => {})} 
                        style={styles.filterButtonCollapsed}
                      />
                      <FilterDropdown 
                        label="Year" 
                        value={yearFilter}
                        options={yearOptions}
                        onSelect={onYearFilterChange || (() => {})} 
                        style={styles.filterButtonCollapsed}
                      />
                      <FilterDropdown 
                        label="Created by" 
                        value={createdByFilter}
                        options={createdByOptions}
                        onSelect={onCreatedByFilterChange || (() => {})} 
                        style={styles.filterButtonCollapsed}
                      />
                      <FilterDropdown 
                        label="Modified by" 
                        value={modifiedByFilter}
                        options={modifiedByOptions}
                        onSelect={onModifiedByFilterChange || (() => {})} 
                        style={styles.filterButtonCollapsed}
                      />
                    </View>
                  ) : (
                    // Expanded state - two rows with 5 filters each
                    <View style={styles.filterRowsExpanded}>
                      {/* First row - 5 filters */}
                      <View style={styles.filterRowExpanded}>
                        <FilterDropdown 
                          label="Period" 
                          value={periodFilter}
                          options={periodOptions}
                          onSelect={onPeriodFilterChange || (() => {})}
                          style={styles.filterButtonExpanded}
                        />
                        <FilterDropdown 
                          label="Week" 
                          value={weekFilter}
                          options={weekOptions}
                          onSelect={onWeekFilterChange || (() => {})} 
                          style={styles.filterButtonExpanded}
                        />
                        <FilterDropdown 
                          label="Year" 
                          value={yearFilter}
                          options={yearOptions}
                          onSelect={onYearFilterChange || (() => {})} 
                          style={styles.filterButtonExpanded}
                        />
                        <FilterDropdown 
                          label="Created by" 
                          value={createdByFilter}
                          options={createdByOptions}
                          onSelect={onCreatedByFilterChange || (() => {})} 
                          style={styles.filterButtonExpanded}
                        />
                        <FilterDropdown 
                          label="Modified by" 
                          value={modifiedByFilter}
                          options={modifiedByOptions}
                          onSelect={onModifiedByFilterChange || (() => {})} 
                          style={styles.filterButtonExpanded}
                        />
                      </View>
                      
                      {/* Second row - 5 filters */}
                      <View style={styles.filterRowExpanded}>
                        <View style={isSmallScreen ? styles.filterButtonExpandedSmall : styles.filterButtonExpanded}>
                          <DLORangeFilter 
                            label="Headcount" 
                            value={headcountRangeFilter}
                            onApply={onHeadcountRangeFilterChange || (() => {})}
                          />
                        </View>
                        <View style={isSmallScreen ? styles.filterButtonExpandedSmall : styles.filterButtonExpanded}>
                          <DLORangeFilter 
                            label="Actual KCPH" 
                            value={actualKcphRangeFilter}
                            onApply={onActualKcphRangeFilterChange || (() => {})}
                          />
                        </View>
                        <View style={isSmallScreen ? styles.filterButtonExpandedSmall : styles.filterButtonExpanded}>
                          <DLORangeFilter 
                            label="Forecasted Hours" 
                            value={forecastedHoursRangeFilter}
                            onApply={onForecastedHoursRangeFilterChange || (() => {})}
                          />
                        </View>
                        <View style={isSmallScreen ? styles.filterButtonExpandedSmall : styles.filterButtonExpanded}>
                          <DLORangeFilter 
                            label="Forecasted Cases" 
                            value={forecastedCasesRangeFilter}
                            onApply={onForecastedCasesRangeFilterChange || (() => {})}
                          />
                        </View>
                        <View style={isSmallScreen ? styles.filterButtonExpandedSmall : styles.filterButtonExpanded}>
                          <DLORangeFilter 
                            label="Actual Cases" 
                            value={actualCasesRangeFilter}
                            onApply={onActualCasesRangeFilterChange || (() => {})}
                          />
                        </View>
                      </View>
                    </View>
                  )}
                </View>

                {/* Separator between filters and buttons */}
                <View style={styles.filterButtonSeparator} />

                {/* Action Buttons Container - 25% width */}
                <View style={[styles.actionButtonsWrapper, { flex: responsiveStyles.actionButtonsWrapperFlex }]}>
                  {/* Reset All button when expanded */}
                  <ResetAllButton onPress={handleResetAllPress} />

                  {/* All Filters button with badge when expanded */}
                  <AllFiltersButton
                    isExpanded={isFiltersExpanded}
                    filterCount={filterCount}
                    onPress={handleAllFiltersPress}
                  />
                </View>
              </View>
            </View>
          </View>
        </View>

        {/* Bulk Actions Section */}
        <View style={styles.bulkActionsContainer}>
          <View style={[styles.bulkActionsContent, { 
            paddingVertical: responsiveStyles.bulkActionsPadding,
            paddingHorizontal: responsiveStyles.headerPadding,
          }]}>
            {/* <Text style={styles.selectedItemsText}>
              {selectedCount} item (s) selected
            </Text> */}

            <View style={styles.actionsRow}>
              <ActionButton
                icon={<EditIcon />}
                label={selectedScheduleData?.uiStatus === 'Closed' || selectedScheduleData?.uiStatus === 'Finalized' ? 'View' : 'Edit'}
                disabled={selectedCount !== 1} // Only enable for single selection
                onPress={onEditPress}
              />
              <ActionButton
                icon={<ContentCopyIcon />}
                label="Copy Schedule"
                disabled={selectedCount !== 1} // Only enable for single selection
                onPress={selectedScheduleData ? handleCopyScheduleFromTablePress : onCopySchedulePress}
              />
              <ActionButton
                icon={<DeleteIcon />}
                label="Delete"
                disabled={selectedCount !== 1}
                onPress={onDeletePress}
              />
            </View>
          </View>
        </View>

        {/* Copy Schedule Modal */}
        <DLOCreateScheduleModal
          visible={showCopyScheduleModal}
          onClose={handleCopyScheduleModalClose}
          onCopySchedule={handleCopyScheduleFromModal}
          currentPeriod={currentPeriod}
          currentWeek={currentWeek}
          currentYear={currentYear}
          scheduleTableData={[]} // Empty array to hide favorite field
          isCopyMode={true}
        />
      </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#FBFCFE',
  },
  headerContainer: {
    borderBottomWidth: 2,
    borderBottomColor: '#D7E5F8',
  },
  headerContent: {
    backgroundColor: '#FBFCFE',
    paddingTop: 12,
    maxHeight: 48,
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  title: {
    color: '#3A3A3A',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Bold',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: 24,
    fontWeight: '700',
    lineHeight: 32,
    alignSelf: 'flex-start',
    flex: 1,
  },
  filtersContainer: {
    backgroundColor: '#D7E5F8',
  },
  filtersContent: {
    backgroundColor: '#FBFCFE',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start', // Changed to flex-start to handle different heights
    gap: 12,
  },
  filtersRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    width: '100%',
  },
  filterGroup: {
    maxWidth: '100%',
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 8,
  },
  filterButton: {
    flexDirection: 'row',
    height: 32,
    paddingHorizontal: 16,
    justifyContent: 'center',
    alignItems: 'center',
    gap: 4,
    borderRadius: 16, // More rounded to match the design
    borderWidth: 1,
    borderColor: '#D7E5F8',
    backgroundColor: '#FBFCFE',
  },
  filterButtonText: {
    color: '#005CBC',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 20,
  },
  separator: {
    width: 1,
    height: 24,
    backgroundColor: '#D7E5F8',
  },
  allFiltersButton: {
    flexDirection: 'row',
    height: 32,
    paddingHorizontal: 16,
    paddingVertical: 4,
    justifyContent: 'center',
    alignItems: 'center',
    gap: 4,
    borderRadius: 16, // More rounded to match the design
    backgroundColor: '#005CBC',
    // marginLeft: 'auto',
  },
  allFiltersButtonText: {
    color: '#FFF',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Bold',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: 14,
    fontWeight: '700',
    lineHeight: 20,
    maxWidth: 150,
  },
  allFiltersButtonExpanded: {
    backgroundColor: '#005CBC',
    paddingHorizontal: 16,
    paddingVertical: 4,
  },
  allFiltersButtonTextExpanded: {
    color: '#FFF',
  },
  badge: {
    height: 16,
    minWidth: 16,
    paddingHorizontal: 4,
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 8,
    backgroundColor: '#0078A7',
    marginLeft: 4,
  },
  badgeText: {
    color: '#FFF',
    textAlign: 'center',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Bold',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: 10,
    fontWeight: '700',
    lineHeight: 14,
    textTransform: 'uppercase',
  },
  resetAllButton: {
    flexDirection: 'row',
    height: 32,
    paddingHorizontal: 12,
    paddingVertical: 4,
    justifyContent: 'center',
    alignItems: 'center',
    gap: 4,
    borderRadius: 16,
    backgroundColor: 'transparent',
    borderWidth: 0,
  },
  resetAllButtonText: {
    color: '#005CBC',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Bold',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: 14,
    fontWeight: '700',
    lineHeight: 20,
    maxWidth: 150,
  },
  bulkActionsContainer: {
    backgroundColor: '#D7E5F8',
  },
  bulkActionsContent: {
    backgroundColor: '#EFF5FC',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  selectedItemsText: {
    color: '#005CBC',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Bold',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: 14,
    fontWeight: '700',
    lineHeight: 20,
  },
  actionsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    flexWrap: 'wrap',
  },
  actionButton: {
    height: 24,
    flexDirection: 'row',
    paddingHorizontal: 8,
    paddingVertical: 4,
    justifyContent: 'center',
    alignItems: 'center',
    gap: 2,
    borderRadius: 12, // More rounded to match the design
    borderWidth: 1,
    borderColor: '#DFDFDF',
    backgroundColor: '#FBFCFE',
  },
  actionButtonDisabled: {
    opacity: 1, // Keep opacity at 1 as the design shows disabled state via color
  },
  actionButtonText: {
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Bold',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: 12,
    fontWeight: '700',
    lineHeight: 18,
    maxWidth: 150,
  },
  actionButtonTextDisabled: {
    color: '#A0A0A0',
  },
  filterDropdownContainer: {
    // Exact same styling as filterButton to maintain visual consistency
    flexDirection: 'row',
    height: 32,
    paddingHorizontal: 16,
    justifyContent: 'center',
    alignItems: 'center',
    gap: 4,
    borderRadius: 24,
    borderWidth: 1,
    borderColor: '#D7E5F8',
    backgroundColor: '#FBFCFE',
    overflow: 'hidden',
  },
  filterDropdownInner: {
    // Override DLODropdown internal styles to be transparent
    flex: 1,
    backgroundColor: 'transparent',
    borderWidth: 0,
    margin: 0,
    padding: 0,
    height: '100%',
  },
  filterButtonTextSelected: {
    color: '#005CBC',
    fontWeight: '600',
  },
  dropdownOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.1)',
  },
  dropdownMenu: {
    backgroundColor: '#FBFCFE',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#D7E5F8',
    maxHeight: 200,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.25,
    shadowRadius: 8,
    elevation: 8,
  },
  dropdownScrollView: {
    maxHeight: 200,
  },
  dropdownItem: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#F0F0F0',
  },
  dropdownItemSelected: {
    backgroundColor: '#E8F2FF',
  },
  dropdownItemText: {
    color: '#3A3A3A',
    fontSize: 14,
    fontWeight: '400',
  },
  dropdownItemTextSelected: {
    color: '#005CBC',
    fontWeight: '600',
  },
  dropdownSeparator: {
    height: 1,
    backgroundColor: '#D7E5F8',
    marginHorizontal: 8,
  },
  // New filter layout styles
  filtersWrapper: {
    flex: 0.75, // 75% width
    marginRight: 16,
  },
  filterButtonSeparator: {
    width: 1,
    height: 32,
    backgroundColor: '#D7E5F8',
    // alignSelf: 'flex-start',
  },
  actionButtonsWrapper: {
    flex: 0.25, // 25% width
    flexDirection: 'row',
    alignItems: 'flex-start', // Align with filters section
    justifyContent: 'flex-start',
    gap: 8,
    minHeight: 32, // Ensure minimum height to match filter buttons
  },
  filterRowSingle: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 8,
  },
  filterRowsExpanded: {
    gap: 12, // Gap between the two rows
  },
  filterRowExpanded: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    justifyContent: 'flex-start',
    flexWrap: 'wrap', // Allow wrapping on smaller screens
  },
  filterButtonCollapsed: {
    // Apply consistent width for collapsed state as well
    width: 172,
    minWidth: 172,
    maxWidth: 172,
    flex: 0,
    flexShrink: 1, // Allow shrinking on smaller screens only if needed
  },
  filterButtonExpanded: {
    width: 172, // Fixed width as requested
    minWidth: 172,
    maxWidth: 172,
    // Override any flex styles from parent
    flex: 0,
    flexShrink: 0,
  },
  // Small screen responsive override
  filterButtonExpandedSmall: {
    width: '48%', // Take roughly half width on small screens
    minWidth: 140,
    maxWidth: 200,
    flex: 0,
    flexShrink: 1,
  },
});

export default DLOScheduleTableHeader;
